import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { NextPageContext } from 'next';
import { getCategoryPath, makeHostSum } from '@/utils/helpers';
import { apolloClient } from '@/api/apolloClient';
import {
  GetSeoResponse,
  SeoFullData,
  SeoMetadata,
} from '@/api/types/seo.types';
import { GET_SEO } from '@/api/queries/seo.queries';
import {
  ROOT_CATEGORY_SLUG,
  SINGLE_AD_PAGE_SLUGS_COUNT,
} from '@/constants/common';
import { parseQuery } from '@/hooks/useParseQuery/useParseQuery.helpers';
import { GetRegionsResponse } from '@/api/types/regions.types';
import {
  EDIT_AD_PAGE_SLUG,
  NEW_AD_PAGE_SLUG,
} from '@/hooks/usePageType/usePageType.constants';

async function getSeoMetaData(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext,
  asPath: string
): Promise<SeoMetadata> {
  const path = asPath || '/';
  const seoData = await apolloClient<GetSeoResponse>(
    client,
    { query: GET_SEO, variables: { path }, fetchPolicy: 'no-cache' },
    context
  );

  return {
    title: seoData?.data.seoMetadata.title || null,
    ogTitle: seoData?.data.seoMetadata.title || null,
    description: seoData?.data.seoMetadata.description || null,
    jsonLd: seoData?.data.seoMetadata.jsonLd || null,
  };
}

export async function getSeoMetaDataForCollection(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext,
  path: string
): Promise<SeoMetadata> {
  const seoData = await apolloClient<GetSeoResponse>(
    client,
    { query: GET_SEO, variables: { path }, fetchPolicy: 'no-cache' },
    context
  );

  return {
    title: seoData?.data.seoMetadata.title || null,
    ogTitle: seoData?.data.seoMetadata.title || null,
    description: seoData?.data.seoMetadata.description || null,
    jsonLd: seoData?.data.seoMetadata.jsonLd || null,
  };
}

async function getSeoMetaDataForSingleAd(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext,
  adLegacyId: number,
  path: string
): Promise<SeoMetadata> {
  const seoData = await apolloClient<GetSeoResponse>(
    client,
    {
      query: GET_SEO,
      variables: { adLegacyId, path },
      fetchPolicy: 'no-cache',
    },
    context
  );

  return {
    title: seoData?.data.seoMetadata.title || null,
    ogTitle: seoData?.data.seoMetadata.adTitle || null,
    description: seoData?.data.seoMetadata.description || null,
    jsonLd: seoData?.data.seoMetadata.jsonLd || null,
  };
}

async function getSeoMetaDataForSearchResults(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext,
  categoryId: string | null,
  regionsData: GetRegionsResponse | null = null
): Promise<SeoMetadata> {
  const { query } = context;
  const categoryPath = getCategoryPath(context.asPath);
  const isRootCategory = categoryPath === ROOT_CATEGORY_SLUG;

  const { keywords, price, regionId, propertyOptions, userLegacyId, isShop } =
    parseQuery(query, regionsData);

  const filters = {
    price,
    regionId,
    propertyOptions,
    userLegacyId,
    categoryId,
    isShop,
  };

  const seoData = await apolloClient<GetSeoResponse>(
    client,
    {
      query: GET_SEO,
      variables: { path: categoryPath, keywords, filters },
      fetchPolicy: 'no-cache',
    },
    context
  );

  const title = seoData?.data.seoMetadata.title || null;

  return {
    title,
    ogTitle: !isRootCategory ? title : '',
    description: seoData?.data.seoMetadata.description || null,
    jsonLd: seoData?.data.seoMetadata.jsonLd || null,
    h1: seoData?.data.seoMetadata.h1 || null,
  };
}

export async function getSeoFullData(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext,
  categoryId: string | null = null,
  regionsData: GetRegionsResponse | null = null
): Promise<SeoFullData> {
  const hostSum = makeHostSum({
    asPath: context.asPath ?? context.req?.url,
    req: context.req,
  });

  const asPath = context.asPath?.split('?')[0].slice(1) || '';
  const slugs = asPath.split('/');

  const isSingleAdPage =
    slugs.length === SINGLE_AD_PAGE_SLUGS_COUNT &&
    /^[0-9]+$/.test(slugs[slugs.length - 1]);
  const adLegacyId = Number(slugs[SINGLE_AD_PAGE_SLUGS_COUNT - 1]);

  const isNewAdPage = asPath.endsWith(`/${NEW_AD_PAGE_SLUG}`);
  const isEditAdPage = asPath.endsWith(`/${EDIT_AD_PAGE_SLUG}`);

  if (isNewAdPage || isEditAdPage) {
    return { title: '', ogTitle: '', description: '', jsonLd: null, hostSum };
  }

  if (isSingleAdPage && adLegacyId) {
    const seoMetaData = await getSeoMetaDataForSingleAd(
      client,
      context,
      adLegacyId,
      asPath
    );

    return { ...seoMetaData, hostSum };
  }

  if (asPath?.includes(ROOT_CATEGORY_SLUG)) {
    const seoMetaData = await getSeoMetaDataForSearchResults(
      client,
      context,
      categoryId,
      regionsData
    );

    return { ...seoMetaData, hostSum };
  }

  const { title, jsonLd } = await getSeoMetaData(client, context, asPath);

  return { title, ogTitle: '', description: '', jsonLd, hostSum };
}
